/**
 * Alta guiada — la lógica pura, sin React.
 *
 * Cargar un producto no es llenar un formulario de diecisiete campos: es
 * contestar tres o cuatro preguntas que dependen de QUÉ se está cargando. Un
 * iPhone sellado se define por modelo y memoria (y cotiza en dólares); un
 * perfume por marca, nombre y mililitros; un vaper por marca y puffs. El
 * nombre del catálogo se arma solo, con el mismo formato que ya usa todo el
 * sistema, y el que carga no tiene que inventar nada.
 *
 * Los usados no pasan por acá (tienen su propio asistente con ficha por
 * unidad), y "otro" abre el formulario completo con el motor de precio: este
 * wizard cubre lo frecuente, no reemplaza al form para lo raro.
 */
import { convertir, despejarPrecio, formatear, formatearNumero, type Moneda } from "@tora/shared";

import {
  esIphoneElegido,
  faltaDelModelo,
  marcaDelEquipo,
  nombreDeEquipo,
} from "@/components/iphones/equipo";

import { etiquetaCapacidad } from "@/components/iphones/logica";
import { type NuevoProducto, type Producto, type Rubro } from "@/lib/tipos";

import {
  EQUIPO_APPLE_INICIAL,
  RUBRO_DE_FAMILIA,
  faltaDelEquipoApple,
  nombreDeEquipoApple,
  preguntaColor,
  type EquipoApple,
} from "./equiposApple";

// ─────────────────────────────────────────────────────────────────────────────
// Qué se puede cargar
// ─────────────────────────────────────────────────────────────────────────────

/** Lo que el wizard resuelve solo. Usado y "otro" delegan en sus forms. */
export type TipoAlta =
  "iphone" | "celular" | "equipo" | "accesorio" | "perfume" | "vaper" | "apedido";

export const OPCIONES_ALTA: ReadonlyArray<{
  tipo: TipoAlta;
  titulo: string;
  detalle: string;
}> = [
  { tipo: "iphone", titulo: "iPhone sellado", detalle: "Modelo y memoria. Cotiza en dólares." },
  {
    tipo: "celular",
    titulo: "Celular de otra marca",
    detalle: "Nuevo, en caja: marca, modelo y memoria.",
  },
  { tipo: "equipo", titulo: "Equipo", detalle: "Mac, iPad, tablets, relojes, consolas, cámaras." },
  { tipo: "accesorio", titulo: "Accesorio", detalle: "Cables, fundas, audio, vasos…" },
  { tipo: "perfume", titulo: "Perfume", detalle: "Marca, nombre y mililitros." },
  { tipo: "vaper", titulo: "Vaper", detalle: "Marca y puffs." },
  {
    tipo: "apedido",
    titulo: "A pedido",
    detalle: "Computadoras, consolas, cámaras: lo que se consigue y no se stockea.",
  },
];

/**
 * A qué rubro va cada tipo. El accesorio elige entre los suyos.
 *
 * Lo «a pedido» elige entre todos: es una marca de la ficha, no una familia.
 * Hasta el 29/09/2026 caía en un rubro «A pedido» propio, que no estaba
 * publicado —lo cargado ahí no salía en la tienda— y tampoco decía qué era la
 * cosa: una MacBook vive en Apple aunque se encargue (0099).
 */
export const RUBRO_POR_TIPO: Record<
  Exclude<TipoAlta, "accesorio" | "apedido" | "equipo">,
  string
> = {
  iphone: "Apple",
  celular: "Celulares",
  perfume: "Arabian Scents",
  vaper: "Californian Vapes",
};

/**
 * Entre que rubros elige el alta de un accesorio. Las familias de 0043 tienen
 * que estar: si el alta solo ofreciera «Accesorios», cada cable nuevo volveria
 * al cajon generico y la division se pudriria sola en un par de meses.
 */
export const RUBROS_ACCESORIO = [
  "Cables y adaptadores",
  "Cargadores y baterías",
  "Audio",
  "Fundas y protección",
  "Soportes",
  "Simil Apple",
  "Simil Stanley",
  "JBL",
  "SoundPeats",
  "Accesorios",
];

// ─────────────────────────────────────────────────────────────────────────────
// El formulario
// ─────────────────────────────────────────────────────────────────────────────

export interface FormAlta {
  // iPhone
  /**
   * El modelo armado: "16 PRO MAX". Se elige en dos pasos —la generacion y la
   * variante— pero viaja como uno, porque es uno: antes eran dos campos y cada
   * pantalla los volvia a juntar a su manera.
   *
   * Nunca se escribe a mano si es un iPhone. Cuando era texto libre, "16 pro
   * max" / "16 PROMAX" / "16 Pro Max" eran tres productos distintos para la
   * base, y nada lo podia corregir despues.
   */
  modelo: string;
  memoriaGb: number | null;
  /** La RAM de un celular de otra marca: «8» en «REDMI NOTE 15 8/256 GB». */
  ramGb: number | null;
  color: string;
  // Perfume / vaper
  marca: string;
  nombre: string;
  ml: number | null;
  inspirado: string;
  puffs: number | null;
  /** Los ml del líquido de un vaper; el perfume usa `ml`. */
  mlVape: number | null;
  sabores: string;
  // Equipo / a pedido: una Mac, un iPad… elegidos con botones
  equipo: EquipoApple;
  // Accesorio / a pedido
  nombreLibre: string;
  rubroId: number | null;
  /** Quién lo tiene, para los «a pedido»: es a quién llamar cuando confirmen. */
  proveedorId: number | null;
  // Comunes
  costo: number | null;
  /**
   * El costo tiene su propia moneda: un cable se compra en dólares al
   * importador y se vende en pesos en el mostrador. Arranca igual que la del
   * precio, pero el selector de al lado del costo la cambia sin tocar la venta.
   */
  monedaCosto: Moneda;
  precio: number | null;
  moneda: Moneda;
  unidades: number | null;
}

export const FORM_ALTA_INICIAL: FormAlta = {
  modelo: "",
  memoriaGb: null,
  ramGb: null,
  color: "",
  marca: "",
  nombre: "",
  ml: 100,
  inspirado: "",
  puffs: null,
  mlVape: null,
  sabores: "",
  equipo: EQUIPO_APPLE_INICIAL,
  nombreLibre: "",
  rubroId: null,
  proveedorId: null,
  costo: null,
  monedaCosto: "ARS",
  precio: null,
  moneda: "ARS",
  unidades: null,
};

/** La moneda con la que arranca cada tipo. El iPhone sigue al dólar. */
export function monedaInicial(tipo: TipoAlta): Moneda {
  return tipo === "iphone" || tipo === "celular" || tipo === "equipo" || tipo === "apedido"
    ? "USD"
    : "ARS";
}

// ─────────────────────────────────────────────────────────────────────────────
// Qué se pregunta de cada cosa
// ─────────────────────────────────────────────────────────────────────────────

/**
 * LO QUE CAMBIA ENTRE UNA CATEGORÍA Y OTRA ES ESTA LISTA, Y NADA MÁS.
 *
 * Antes cada tipo tenía su propio bloque de JSX: uno preguntaba en una
 * columna, otro en dos, uno marcaba lo obligatorio y otro no, y el orden de
 * las preguntas no era el mismo. Cargar un perfume y cargar un vaper se
 * sentían dos formularios distintos porque lo eran.
 *
 * Ahora cada tipo declara sus campos y el diálogo los dibuja con el mismo
 * molde: mismo orden (lo que nombra al producto primero, los números después,
 * lo opcional al final), misma grilla, mismo asterisco en lo obligatorio y
 * mismo lugar para el error. Agregar una categoría es agregar una entrada acá.
 */
export type ClaveCampo =
  | "modelo"
  | "memoriaGb"
  | "ramGb"
  | "color"
  | "marca"
  | "nombre"
  | "ml"
  | "inspirado"
  | "puffs"
  | "mlVape"
  | "sabores"
  | "nombreLibre"
  | "rubroId"
  | "proveedorId";

export interface CampoAlta {
  clave: ClaveCampo;
  /**
   * Con qué primitivo se contesta. `memoria` y `rubro` son grupos de chips;
   * `proveedor` y `familia` son un `SelectNativo` —en el mostrador se carga
   * desde el celular, y ahí el picker del sistema le gana a cualquier listbox.
   * `familia` es el rubro elegido entre todos, demasiados para chips.
   */
  control:
    | "texto"
    | "cantidad"
    | "memoria"
    | "ram"
    | "rubro"
    | "familia"
    | "proveedor"
    | "modelo"
    | "otraMarca"
    | "color"
    | "equipoApple"
    | "colorEquipo"
    | "marcaLibre"
    | "chips";
  label: string;
  ayuda?: string;
  placeholder?: string;
  requerido?: boolean;
  /** Lo que se lee debajo del campo cuando falta. Sin esto no es obligatorio. */
  falta?: string;
  /** Media grilla desde `sm`. Por defecto ocupa el ancho entero. */
  medio?: boolean;
  /**
   * Para `chips`: lo que se elige con un toque. Siempre hay un «Otra» que abre
   * el campo para escribir lo que no está. Números si el campo es numérico.
   */
  opciones?: ReadonlyArray<string> | ReadonlyArray<number>;
}

export const CAMPOS_POR_TIPO: Record<TipoAlta, ReadonlyArray<CampoAlta>> = {
  iphone: [
    {
      clave: "modelo",
      control: "modelo",
      label: "Modelo",
      requerido: true,
      // Lo que falta lo dice `faltaDelModelo`, que distingue "no elegiste nada"
      // de "elegiste el 18 y falta decir cuál de los dos".
      falta: "¿Qué modelo es? (17 Pro, 16, 15 Plus…)",
    },
    {
      clave: "memoriaGb",
      control: "memoria",
      label: "Memoria",
      requerido: true,
      falta: "Elegí la memoria.",
    },
    {
      clave: "color",
      control: "color",
      label: "Color",
      ayuda: "«Todos» si se consigue en cualquiera de los del modelo.",
    },
  ],
  /*
   * Un Samsung o un Motorola nuevo. Se pregunta igual que un usado de otra
   * marca —los mismos controles—: la marca con botones, el modelo escrito y
   * la memoria con botones. El nombre sale de ahí: «SAMSUNG A57 5G 256 GB».
   */
  celular: [
    {
      clave: "modelo",
      control: "otraMarca",
      label: "Marca y modelo",
      requerido: true,
      // Aca no hay botones de iPhone: vacio, lo que falta es la marca.
      falta: "Elegí la marca y escribí el modelo (ej: A57 5G).",
    },
    {
      // Como la escribe el proveedor y como la pregunta el cliente: «8/256».
      clave: "ramGb",
      control: "ram",
      label: "Memoria RAM",
      ayuda: "Si no la sabés, dejala: el nombre sale sin ella.",
    },
    {
      clave: "memoriaGb",
      control: "memoria",
      label: "Memoria",
      requerido: true,
      falta: "Elegí la memoria.",
    },
  ],
  equipo: [
    {
      // Mac, iPad, Watch y AirPods con botones; «Otro equipo» es el texto de
      // siempre. Ver `equiposApple.ts`.
      clave: "nombreLibre",
      control: "equipoApple",
      label: "¿Qué equipo es?",
      placeholder: "CONSOLA PS5 SLIM 1TB",
      requerido: true,
      falta: "Falta el nombre (ej: CONSOLA PS5 SLIM 1TB).",
    },
    {
      // Mac, iPad y Watch: el color de las unidades que entran. Opcional, como
      // en el iPhone: la ficha de lista de precios no lo lleva. Sin esto el
      // equipo nacia con el color en null aunque estuviera en la mano.
      clave: "color",
      control: "colorEquipo",
      label: "Color",
      ayuda: "Opcional. Si entran unidades, el de ellas.",
      placeholder: "MIDNIGHT",
    },
    {
      clave: "rubroId",
      control: "familia",
      label: "¿En qué rubro va?",
      ayuda: "Donde se lo encuentra en el mostrador y en la tienda.",
      requerido: true,
      falta: "Falta el rubro.",
    },
  ],
  perfume: [
    {
      clave: "marca",
      control: "chips",
      // Las que más se venden, en ese orden (catálogo del 29/09/2026).
      opciones: [
        "LATTAFA",
        "ARMAF",
        "MAISON ALHAMBRA",
        "AL HARAMAIN",
        "AFNAN",
        "RASASI",
        "FRENCH AVENUE",
      ],
      label: "Marca",
      placeholder: "LATTAFA",
      requerido: true,
      falta: "Falta la marca (ej: LATTAFA).",
    },
    {
      clave: "nombre",
      control: "texto",
      label: "Nombre del perfume",
      placeholder: "Khamrah",
      requerido: true,
      falta: "Falta el nombre del perfume.",
    },
    {
      clave: "ml",
      control: "chips",
      opciones: [100, 105, 50, 30],
      label: "Mililitros",
      placeholder: "100",
    },
    {
      clave: "inspirado",
      control: "texto",
      label: "Inspirado en",
      ayuda: "Va a la descripción del catálogo.",
      placeholder: "Angels' Share de By Kilian",
    },
  ],
  vaper: [
    {
      clave: "marca",
      control: "chips",
      opciones: ["ELFBAR", "IGNITE", "LOST MARY", "BLACKSHEEP", "RABBEATS"],
      label: "Marca",
      placeholder: "ELFBAR",
      requerido: true,
      falta: "Falta la marca (ej: ELFBAR).",
    },
    {
      clave: "puffs",
      control: "cantidad",
      label: "Puffs",
      placeholder: "15000",
      requerido: true,
      falta: "¿Cuántos puffs?",
      medio: true,
    },
    {
      clave: "mlVape",
      control: "chips",
      opciones: [10, 16, 18, 20],
      label: "Mililitros",
      placeholder: "16",
      medio: true,
    },
    {
      clave: "sabores",
      control: "texto",
      label: "Sabores",
      ayuda: "Va a la descripción del catálogo.",
      placeholder: "GRAPE ICE - DOUBLE APPLE",
    },
  ],
  accesorio: [
    {
      clave: "nombreLibre",
      control: "texto",
      label: "Nombre del producto",
      placeholder: "CABLE MALLADO C A C",
      requerido: true,
      falta: "Falta el nombre del producto.",
    },
    { clave: "rubroId", control: "rubro", label: "Rubro" },
    {
      // Opcional. Sin ella el accesorio nacia con la marca en null y el filtro
      // por marca de Catálogo no lo encontraba (un auricular JBL fuera de JBL).
      clave: "marca",
      control: "marcaLibre",
      label: "Marca",
      ayuda: "Opcional. Elegí una de las que ya están para que el filtro las junte.",
      placeholder: "JBL",
    },
  ],
  apedido: [
    {
      // Mac, iPad, Watch y AirPods con botones; «Otro equipo» es el texto de
      // siempre. Ver `equiposApple.ts`.
      clave: "nombreLibre",
      control: "equipoApple",
      label: "¿Qué equipo es?",
      placeholder: "CONSOLA PS5 SLIM 1TB",
      requerido: true,
      falta: "Falta el nombre (ej: CONSOLA PS5 SLIM 1TB).",
    },
    {
      // Mac, iPad y Watch: el color de las unidades que entran. Opcional, como
      // en el iPhone: la ficha de lista de precios no lo lleva. Sin esto el
      // equipo nacia con el color en null aunque estuviera en la mano.
      clave: "color",
      control: "colorEquipo",
      label: "Color",
      ayuda: "Opcional. Si entran unidades, el de ellas.",
      placeholder: "MIDNIGHT",
    },
    {
      clave: "proveedorId",
      control: "proveedor",
      label: "¿Quién lo tiene?",
      ayuda: "Es a quién hay que llamar cuando el cliente confirme.",
      requerido: true,
      falta: "¿Quién lo tiene? Es a quién hay que llamar cuando el cliente confirme.",
    },
    {
      clave: "rubroId",
      control: "familia",
      label: "¿En qué rubro va?",
      ayuda: "Donde se lo encuentra en el mostrador y en la tienda.",
      requerido: true,
      falta: "Falta el rubro: una MacBook va en Apple aunque se encargue.",
    },
  ],
};

/** Una Mac, un iPad, un Watch o unos AirPods: el rubro sale solo. */
export function rubroSeDeduce(form: FormAlta): boolean {
  return form.equipo.familia !== "" && form.equipo.familia !== "OTRO";
}

/** Si el campo quedó sin contestar. Cada control tiene su forma de estar vacío. */
function sinContestar(campo: CampoAlta, form: FormAlta): boolean {
  const valor = form[campo.clave];
  if (campo.control === "equipoApple")
    return form.equipo.familia === "OTRO" ? form.nombreLibre.trim() === "" : false;
  if (campo.control === "texto" || campo.control === "modelo")
    return String(valor ?? "").trim() === "";
  if (campo.control === "cantidad") return valor === null || Number(valor) <= 0;
  if (campo.control === "chips")
    return typeof valor === "number" ? valor <= 0 : String(valor ?? "").trim() === "";
  // El color se contesta con la opcion vacia («Todos»), que es una respuesta y
  // no un hueco: no se valida.
  if (campo.control === "color") return false;
  return valor === null;
}

// ─────────────────────────────────────────────────────────────────────────────
// El nombre
// ─────────────────────────────────────────────────────────────────────────────

export function nombreGenerado(tipo: TipoAlta, form: FormAlta): string {
  switch (tipo) {
    // EL COLOR NO ENTRA EN EL NOMBRE, aunque se pregunte. Va a su columna, que
    // es lo unico que un filtro, una lista y un buscador pueden leer, y se le
    // suma al nombre al MOSTRARLO (`catalogo/nombre.ts`). Escrito adentro del
    // nombre era el mismo error que ya se pago con el "A PEDIDO" de los usados.
    case "iphone":
      return nombreDeEquipo({ modelo: form.modelo, memoriaGb: form.memoriaGb, esUsado: false });
    case "perfume": {
      if (!form.marca.trim() || !form.nombre.trim()) return "";
      const partes = [form.marca.trim().toUpperCase(), form.nombre.trim()];
      if (form.ml) partes.push(`${form.ml}ml`);
      return partes.join(" ");
    }
    case "vaper": {
      if (!form.marca.trim() || !form.puffs) return "";
      // «VAPE ELFBAR 16ML 25.000 PUFFS»: los puffs con punto de miles, como se
      // leen en la caja; los ml solo si se dijeron.
      const partes = ["VAPE", form.marca.trim().toUpperCase()];
      if (form.mlVape) partes.push(`${form.mlVape}ML`);
      partes.push(`${formatearNumero(form.puffs, 0)} PUFFS`);
      return partes.join(" ");
    }
    // Sin marca y modelo no hay nombre: un «256 GB» solo no es un teléfono.
    case "celular": {
      if (faltaDelModelo(form.modelo)) return "";
      if (form.ramGb === null || form.memoriaGb === null)
        return nombreDeEquipo({ modelo: form.modelo, memoriaGb: form.memoriaGb, esUsado: false });
      // «REDMI NOTE 15 8/256 GB»: la RAM y la memoria juntas, como en la caja.
      const base = nombreDeEquipo({ modelo: form.modelo, memoriaGb: null, esUsado: false });
      return `${base} ${form.ramGb}/${etiquetaCapacidad(form.memoriaGb)}`;
    }
    case "equipo":
    case "apedido":
      if (form.equipo.familia !== "OTRO" && form.equipo.familia !== "")
        return nombreDeEquipoApple(form.equipo);
      return form.nombreLibre.trim().toUpperCase();
    case "accesorio":
      return form.nombreLibre.trim().toUpperCase();
  }
}

function descripcionGenerada(tipo: TipoAlta, form: FormAlta): string | null {
  if (tipo === "perfume" && form.inspirado.trim()) return `Inspirado en ${form.inspirado.trim()}`;
  if (tipo === "vaper" && form.sabores.trim()) return form.sabores.trim();
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Validación por paso
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Un error por campo, con la clave del campo que lo tiene. Es lo que deja que
 * el mensaje viva DEBAJO del campo que falta y no en una lista al pie: con
 * cuatro campos en pantalla, "Falta la marca" al pie obliga a buscar cuál.
 */
export function erroresPorCampo(
  tipo: TipoAlta,
  form: FormAlta,
): Partial<Record<ClaveCampo, string>> {
  const errores: Partial<Record<ClaveCampo, string>> = {};
  for (const campo of CAMPOS_POR_TIPO[tipo]) {
    /*
     * El modelo no se valida por "está vacío" sino por "es un teléfono que
     * existe". Elegir la generación y saltear la variante deja "18" —Apple hizo
     * el 18 Pro y el 18 Pro Max, no el 18 a secas— y el campo no está vacío: sin
     * esto se cargaba un IPHONE 18 256 GB con su precio y su stock.
     */
    if (campo.control === "modelo" || campo.control === "otraMarca") {
      const modelo = String(form[campo.clave] ?? "");
      const falta = faltaDelModelo(modelo);
      if (falta) errores[campo.clave] = (modelo.trim() === "" && campo.falta) || falta;
      // Un iPhone nuevo es del lineup y se carga en Sellados, no como celular.
      else if (campo.control === "otraMarca" && esIphoneElegido(modelo))
        errores[campo.clave] = "Un iPhone nuevo se carga en Sellados.";
      continue;
    }
    // El rubro de una Mac o un iPad sale de lo elegido: no se pregunta.
    if (campo.control === "familia" && rubroSeDeduce(form)) continue;
    if (campo.control === "equipoApple") {
      const falta = faltaDelEquipoApple(form.equipo);
      if (falta) errores[campo.clave] = falta;
      else if (sinContestar(campo, form) && campo.falta) errores[campo.clave] = campo.falta;
      continue;
    }
    if (!campo.requerido || !campo.falta) continue;
    if (sinContestar(campo, form)) errores[campo.clave] = campo.falta;
  }
  return errores;
}

/** Los mismos errores, en lista, para quien solo necesita saber si hay. */
export function validarDatos(tipo: TipoAlta, form: FormAlta): string[] {
  return Object.values(erroresPorCampo(tipo, form));
}

function costoComparable(form: FormAlta, cotizacion?: number): number | null {
  if (form.costo === null) return null;
  if (form.monedaCosto === form.moneda) return form.costo;
  if (typeof cotizacion !== "number" || cotizacion <= 0) return null;
  return convertir(form.costo, form.monedaCosto, form.moneda, cotizacion);
}

/**
 * Para comparar precio contra costo cuando están en monedas distintas hace
 * falta la cotización. Sin ella, no se puede saber si US$ 10 es más o menos
 * que $ 12.000, y antes que inventar un aviso falso, se calla.
 */
export function validarPrecio(form: FormAlta, cotizacion?: number): string[] {
  const errores: string[] = [];
  if (form.precio === null || form.precio <= 0) errores.push("Falta el precio de venta.");
  if (form.costo !== null && form.costo < 0) errores.push("El costo no puede ser negativo.");
  const costoEnMonedaDeVenta = costoComparable(form, cotizacion);
  if (
    form.precio !== null &&
    costoEnMonedaDeVenta !== null &&
    costoEnMonedaDeVenta > 0 &&
    form.precio < costoEnMonedaDeVenta
  )
    errores.push("Ojo: el precio está por debajo del costo.");
  if (form.unidades !== null && form.unidades < 0)
    errores.push("Las unidades no pueden ser negativas.");
  return errores;
}

/**
 * UN EQUIPO EN LA MANO TIENE UN COLOR.
 *
 * Un sellado sin color elegido es la FILA DE LISTA DE PRECIOS del modelo: dice
 * cuánto sale el 17 Pro y en qué colores viene, y por eso queda en cero.
 * Cargarle unidades a esa fila es anotar «tengo tres 17 Pro» sin decir de
 * cuáles, que es justo lo que después nadie puede contestar por teléfono.
 *
 * Es la misma distinción que los usados ya tienen entre una referencia y una
 * unidad; lo que separa las dos cosas acá es el color.
 */
export function validarUnidades(tipo: TipoAlta | null, form: FormAlta): string[] {
  if (tipo !== "iphone") return [];
  if ((form.unidades ?? 0) <= 0 || form.color.trim()) return [];
  return [
    "¿De qué color son? Con unidades en la mano hace falta el color: «Todos» es para la fila de lista de precios, que queda en cero.",
  ];
}

// ─────────────────────────────────────────────────────────────────────────────
// Payload
// ─────────────────────────────────────────────────────────────────────────────

function rubroDe(tipo: TipoAlta, form: FormAlta, rubros: Rubro[]): number | null {
  if (tipo === "accesorio") {
    if (form.rubroId !== null) return form.rubroId;
    return rubros.find((r) => r.nombre === "Accesorios")?.id ?? null;
  }
  if (tipo === "apedido" || tipo === "equipo") {
    // Una Mac va a «Mac» sin preguntarlo: el rubro sale de lo que se eligió.
    const familia = form.equipo.familia;
    if (form.rubroId === null && familia !== "" && familia !== "OTRO")
      return rubros.find((r) => r.nombre === RUBRO_DE_FAMILIA[familia])?.id ?? null;
    return form.rubroId;
  }
  return rubros.find((r) => r.nombre === RUBRO_POR_TIPO[tipo])?.id ?? null;
}

/**
 * El precio escrito en el alta, como lo guarda la ficha.
 *
 * Con costo, se despeja el margen que lo da (pendientes, decision 4: escribir
 * el precio no es congelarlo). Antes iba siempre como `precio_manual` y el
 * margen quedaba en cero, el default de la columna: el producto no acompañaba
 * las subas de costo, y destildar «Fijar este precio» lo dejaba al costo.
 *
 * Sin costo no hay margen que despejar y el precio queda fijo, como en la
 * ficha. Tampoco si el costo y el precio van en monedas distintas y no hay
 * cotizacion para cruzarlas.
 */
function precioDelAlta(
  form: FormAlta,
  cotizacion: number,
): Pick<NuevoProducto, "precio_manual" | "markup_tipo" | "markup_valor" | "redondeo"> {
  if (form.precio === null || !(form.costo !== null && form.costo > 0)) {
    return { precio_manual: form.precio };
  }
  if (form.monedaCosto !== form.moneda && !(cotizacion > 0)) {
    return { precio_manual: form.precio };
  }
  const despeje = despejarPrecio({
    costo: form.costo,
    costoMoneda: form.monedaCosto,
    precioDeseado: form.precio,
    precioMoneda: form.moneda,
    markupTipo: "PORCENTAJE",
    redondeo: "NINGUNO",
    cotizacion,
  });
  if (despeje === null) return { precio_manual: form.precio };
  return {
    precio_manual: null,
    markup_tipo: "PORCENTAJE",
    markup_valor: despeje.markupValor,
    redondeo: despeje.redondeo,
  };
}

/**
 * La ayuda del precio de venta, que dice lo que va a pasar de verdad.
 *
 * Salia solo de la moneda: en pesos decia «queda fijo hasta que lo cambies»
 * aunque con costo el alta despeja el margen y el precio SI acompaña al costo
 * (`precioDelAlta`). Ahora sigue la misma cuenta: con costo (y cotizacion, si
 * van en monedas distintas) se guarda el margen; sin costo, el precio fijo.
 */
export function ayudaDelPrecio(form: FormAlta, cotizacion?: number): string {
  const conMargen =
    form.costo !== null &&
    form.costo > 0 &&
    (form.monedaCosto === form.moneda || (cotizacion ?? 0) > 0);
  const regla = conMargen
    ? "Se guarda el margen: si sube el costo, el precio acompaña."
    : "Sin costo el precio queda fijo hasta que lo cambies.";
  return form.moneda === "USD"
    ? `${regla} En dólares, además, se remarca en pesos con la cotización del día.`
    : regla;
}

/**
 * El producto que se crea. El precio va en la moneda elegida: en USD queda
 * atado al dólar y se remarca solo con la cotización.
 */
export function aProductoNuevo(
  tipo: TipoAlta,
  form: FormAlta,
  rubros: Rubro[],
  cotizacion = 0,
): NuevoProducto {
  return {
    nombre: nombreGenerado(tipo, form),
    /**
     * LA FICHA DEL EQUIPO VA A SUS COLUMNAS, NO ADENTRO DEL NOMBRE.
     *
     * El color ya iba; la memoria no, y quedaba escrita solo en el nombre. Por
     * eso Inventario listaba dos sellados del mismo modelo con el mismo texto, y las
     * vistas que salieron a leer `memoria_gb` la encontraban en null en todos
     * los sellados cargados por esta pantalla.
     *
     * Un color vacio no se manda: el sellado sin color es la fila de lista de
     * precios del modelo, que viene en todos.
     */
    ...(tipo === "iphone"
      ? {
          ...(form.color.trim() ? { color: form.color.trim().toUpperCase() } : {}),
          ...(form.memoriaGb !== null ? { memoria_gb: form.memoriaGb } : {}),
        }
      : {}),
    // Lo de Apple lleva su marca, como los que ya estan cargados. El alta la
    // dejaba vacia en los iPhone, Mac, iPad, Watch y AirPods, y el filtro de
    // marca no los encontraba.
    ...(tipo === "iphone" ||
    ((tipo === "equipo" || tipo === "apedido") &&
      form.equipo.familia !== "" &&
      form.equipo.familia !== "OTRO")
      ? { marca: "Apple" }
      : {}),
    // La marca del celular va a su columna, como en los que ya estan
    // («Samsung»): es lo que filtra «Otras marcas». Y la memoria a la suya.
    ...(tipo === "celular"
      ? {
          marca: marcaDelEquipo(form.modelo),
          ...(form.memoriaGb !== null ? { memoria_gb: form.memoriaGb } : {}),
        }
      : {}),
    // Mac, iPad y Watch con el color elegido, a su columna como el del iPhone.
    ...((tipo === "equipo" || tipo === "apedido") && preguntaColor(form.equipo) && form.color.trim()
      ? { color: form.color.trim().toUpperCase() }
      : {}),
    // La marca del perfume y del vaper ya se preguntaba y armaba el nombre,
    // pero no llegaba a su columna: el producto nacia con la marca en null y
    // el filtro por marca no lo encontraba. Van en mayusculas, como las que ya
    // estan («LATTAFA», «ELFBAR»). La del accesorio es opcional y va como se
    // eligio, que es como la escriben las cargadas («JBL», «Samsung»).
    ...((tipo === "perfume" || tipo === "vaper") && form.marca.trim()
      ? { marca: form.marca.trim().toUpperCase() }
      : {}),
    ...(tipo === "accesorio" && form.marca.trim() ? { marca: form.marca.trim() } : {}),
    descripcion: descripcionGenerada(tipo, form),
    rubro_id: rubroDe(tipo, form, rubros),
    precio_moneda: form.moneda,
    ...precioDelAlta(form, cotizacion),
    // La moneda del costo va SIEMPRE, aunque el costo quede vacío: sin ella la
    // base le pone dólares por defecto, y al corregirlo después «1600»
    // quedaba en US$ 1.600 ($ 2.544.000 al dólar del día).
    costo_moneda: form.monedaCosto,
    ...(form.costo !== null && form.costo > 0 ? { costo: form.costo } : {}),
    // Lo que se encarga no es stock propio: queda en cero y marcado, para que
    // el mostrador diga "A pedido" y no "Sin stock", y para que venderlo
    // dispare la compra al proveedor en vez de dejar el stock en negativo.
    ...(tipo === "apedido" ? { a_pedido: true, proveedor_id: form.proveedorId } : {}),
    publicado: false,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Revisión
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Las palabras que identifican un producto, sin la forma de escribirlas.
 *
 * El alta arma el nombre con un formato («MACBOOK AIR M4 13" 16/512 GB») y lo
 * cargado antes tiene otro («MACBOOK AIR M4 13" 16GB 512GB»). Separando los
 * numeros de las unidades y sacando GB, TB, MM, comillas y barras, las dos
 * formas dicen lo mismo: {MACBOOK, AIR, M4, 13, 16, 512}.
 *
 * Tambien se separan las letras y los numeros pegados, y se saca el «+»: el
 * proveedor escribe «520BT» y el que carga «520 BT», y el «+» de «GPS + CELL»
 * quedaba como una palabra mas. Separado, «M4» es {M, 4} de los dos lados, que
 * compara igual.
 */
function palabrasDelProducto(nombre: string): Set<string> {
  const limpio = nombre
    .toUpperCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/(\d)([A-Z])/g, "$1 $2")
    .replace(/([A-Z])(\d)/g, "$1 $2")
    .replace(/["'/()\-·,+]/g, " ");
  const palabras = new Set(
    limpio
      .split(/\s+/)
      .filter((p) => p !== "" && !["GB", "TB", "MM", "DE", "CON", "Y"].includes(p)),
  );
  /*
   * LO QUE EL NOMBRE VIEJO DABA POR SENTADO.
   *
   * Un iPad sin CELL es WiFi: lo cargado antes no lo escribía («IPAD AIR M4
   * 11" 128 GB») y el alta sí («… WIFI»), así que WIFI quedaba de un solo lado
   * y el aviso no salía. Y todo AirPods Pro tiene ANC: la AP-017 lo escribía
   * con adornos («TRADUCTOR + ANC + tips de silicona») y el alta no, con el
   * mismo resultado. Los dos se crearon duplicados sin «¿Es alguno de estos?».
   */
  if (palabras.has("WI") && palabras.has("FI")) {
    palabras.delete("WI");
    palabras.delete("FI");
    palabras.add("WIFI");
  }
  if (palabras.has("IPAD") && !["CELL", "CELLULAR", "LTE"].some((p) => palabras.has(p))) {
    palabras.add("WIFI");
  }
  if (palabras.has("AIRPODS") && palabras.has("PRO")) {
    for (const adorno of ["ANC", "TRADUCTOR", "TIPS", "SILICONA"]) palabras.delete(adorno);
  }
  return palabras;
}

/**
 * Las palabras que hacen OTRO producto aunque todo lo demas coincida.
 *
 * Que las palabras de uno esten todas en el otro no alcanza cuando lo que
 * sobra es la conexion o la variante: «WATCH SE 3 44MM GPS + CELL» contiene a
 * «WATCH SE 3 44 MM GPS» y es otro equipo, con otro precio, y el alta lo
 * proponia como el mismo. Si una de estas aparece de un solo lado, no es el
 * mismo producto.
 */
const PALABRAS_QUE_DISTINGUEN = new Set([
  "CELL",
  "CELLULAR",
  "WIFI",
  "LTE",
  "PRO",
  "MAX",
  "PLUS",
  "MINI",
  "ULTRA",
  "ANC",
  // Las memorias: el de 256 y el de 512 son dos fichas.
  "64",
  "128",
  "256",
  "512",
]);

/**
 * Lo ya cargado que se parece a lo que se esta por crear.
 *
 * El alta no miraba nada: cargar la MacBook Air M4 13" 16/512 creaba una
 * segunda ficha al lado de «MACBOOK AIR M4 13" 16GB 512GB», con otro nombre y
 * otro precio. Parecido es que todas las palabras de uno esten en el otro (en
 * cualquier sentido: «WATCH 11 46MM GPS» esta adentro de «WATCH SERIES 11
 * 46MM GPS»), sin una palabra que distinga de un solo lado, con al menos tres
 * palabras para no avisar por un «CABLE».
 *
 * El mismo nombre se avisa SIEMPRE, sea corto o largo: el piso de tres
 * palabras dejaba pasar «AIRPODS 4» al lado del «AIRPODS 4» que ya estaba.
 */
export function parecidosAlAlta(
  tipo: TipoAlta,
  form: FormAlta,
  existentes: ReadonlyArray<Producto>,
): Producto[] {
  const nuevo = palabrasDelProducto(nombreGenerado(tipo, form));
  if (nuevo.size === 0) return [];
  const contiene = (a: Set<string>, b: Set<string>) => [...b].every((p) => a.has(p));
  const distingue = (a: Set<string>, b: Set<string>) =>
    [...PALABRAS_QUE_DISTINGUEN].some((p) => a.has(p) !== b.has(p));
  return existentes
    .filter((p) => p.activo && !p.es_usado)
    .filter((p) => {
      const otro = palabrasDelProducto(p.nombre);
      if (otro.size === nuevo.size && contiene(nuevo, otro)) return true;
      if (nuevo.size < 3 || otro.size < 3) return false;
      if (distingue(nuevo, otro)) return false;
      return contiene(nuevo, otro) || contiene(otro, nuevo);
    })
    .slice(0, 5);
}

export function resumenAlta(tipo: TipoAlta, form: FormAlta): string[] {
  const lineas = [nombreGenerado(tipo, form)];
  const desc = descripcionGenerada(tipo, form);
  if (desc) lineas.push(desc);
  // El color no va en el nombre (va a su columna), y por eso la revisión no lo
  // mostraba: la MacBook elegida en Midnight se confirmaba sin verlo.
  if (form.color.trim()) lineas.push(`Color: ${form.color.trim()}`);
  // Con el formato de todo el sistema: decia «Costo: 55000 ARS».
  if (form.costo !== null && form.costo > 0)
    lineas.push(`Costo: ${formatear(form.costo, form.monedaCosto)}`);
  lineas.push(`Precio de venta: ${formatear(form.precio ?? 0, form.moneda)}`);
  lineas.push(
    tipo === "apedido"
      ? "Queda «a pedido»: sin stock propio. Venderlo lo encarga al proveedor."
      : form.unidades && form.unidades > 0
        ? form.unidades === 1
          ? "Entra 1 unidad al stock."
          : `Entran ${form.unidades} unidades al stock.`
        : "Sin stock por ahora: entra después por Inventario → Ingreso.",
  );
  if (form.moneda === "USD")
    lineas.push("En dólares: el precio en pesos se remarca solo con la cotización del día.");
  return lineas;
}
